import { colors, fontWeights } from '@/theme'
import type { Order, RefundMethod } from '@/types/order'
import { REFUND_METHODS, REFUND_REASONS } from '@/types/order'
import { zodResolver } from '@hookform/resolvers/zod'
import CloseIcon from '@mui/icons-material/Close'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { Controller, useForm } from 'react-hook-form'
import { NumericFormat } from 'react-number-format'
import { z } from 'zod'

interface SendRefundDialogProps {
  open: boolean
  order: Order
  onClose: () => void
  onSubmit: (data: { amount: number; method: RefundMethod; reason: string }) => Promise<void>
}

const createRefundSchema = (maxAmount: number) =>
  z.object({
    amount: z
      .number({
        required_error: 'Amount is required',
        invalid_type_error: 'Please enter a valid number'
      })
      .positive('Amount must be greater than 0')
      .max(maxAmount, `Amount cannot exceed £${maxAmount.toFixed(2)}`),
    method: z.enum(REFUND_METHODS, {
      required_error: 'Please select a refund method'
    }),
    reason: z.string().optional()
  })

type RefundFormData = z.infer<ReturnType<typeof createRefundSchema>>

export default function SendRefundDialog({ open, order, onClose, onSubmit }: SendRefundDialogProps) {
  const amountRefunded = order.amountRefunded ?? 0
  const totalAvailable = order.totalAvailableToRefund ?? 50.31

  const refundSchema = createRefundSchema(totalAvailable)

  const {
    control,
    handleSubmit: handleFormSubmit,
    formState: { errors, isSubmitting },
    reset
  } = useForm<RefundFormData>({
    resolver: zodResolver(refundSchema),
    defaultValues: {
      amount: undefined,
      method: undefined,
      reason: ''
    }
  })

  const handleSubmit = async (data: RefundFormData) => {
    await onSubmit({
      amount: data.amount,
      method: data.method,
      reason: data.reason || ''
    })
    handleClose()
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <form onSubmit={handleFormSubmit(handleSubmit)}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant='sectionTitle'>Send refund</Typography>
            <IconButton onClick={handleClose} size='small'>
              <CloseIcon fontSize='small' />
            </IconButton>
          </Box>
          <Typography variant='body2' sx={{ mt: 0.5 }}>
            Send refund to patient
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', gap: 4, mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant='fieldLabel' sx={{ display: 'block', mb: 0.5 }}>
                Amount already refunded
              </Typography>
              <Typography variant='fieldValue' sx={{ fontWeight: fontWeights.semibold }}>
                £{amountRefunded.toFixed(2)}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant='fieldLabel' sx={{ display: 'block', mb: 0.5 }}>
                Total available to refund
              </Typography>
              <Typography variant='fieldValue' sx={{ fontWeight: fontWeights.semibold }}>
                £{totalAvailable.toFixed(2)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Tooltip title='Refund the line items above. This will show the total amount to be refunded' arrow>
                  <HelpOutlineIcon sx={{ fontSize: 16, color: colors.brand.primary }} />
                </Tooltip>
                <Typography variant='fieldLabel'>Refund amount</Typography>
              </Box>
              <Controller
                name='amount'
                control={control}
                render={({ field: { onChange, value, ...field } }: { field: any }) => (
                  <NumericFormat
                    {...field}
                    customInput={TextField}
                    fullWidth
                    size='small'
                    placeholder='0.00'
                    value={value ?? ''}
                    onValueChange={values => {
                      const { floatValue } = values
                      onChange(floatValue)
                    }}
                    thousandSeparator=','
                    decimalSeparator='.'
                    decimalScale={2}
                    fixedDecimalScale={false}
                    allowNegative={false}
                    allowLeadingZeros={false}
                    // isAllowed={values => {
                    //   const { floatValue } = values
                    //   return floatValue === undefined || floatValue <= totalAvailable
                    // }}
                    error={!!errors.amount}
                    helperText={errors.amount?.message || `Max: £${totalAvailable.toFixed(2)}`}
                    slotProps={{
                      input: {
                        startAdornment: <InputAdornment position='start'>£</InputAdornment>
                      }
                    }}
                  />
                )}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant='fieldLabel' sx={{ display: 'block', mb: 1 }}>
                Select refund method
              </Typography>
              <Controller
                name='method'
                control={control}
                render={({ field }: { field: any }) => (
                  <FormControl fullWidth size='small' error={!!errors.method}>
                    <Select
                      {...field}
                      value={field.value ?? ''}
                      displayEmpty
                      renderValue={selected => {
                        if (!selected)
                          return <Typography sx={{ color: colors.text.muted }}>Manually or credit card</Typography>
                        return selected === 'Manually' ? 'Manually' : 'Credit Card (Stripe)'
                      }}
                    >
                      {REFUND_METHODS.map(refundMethod => (
                        <MenuItem key={refundMethod} value={refundMethod}>
                          {refundMethod}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.method && (
                      <Typography variant='caption' color='error' sx={{ mt: 0.5, ml: 1.75 }}>
                        {errors.method.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Box>
          </Box>

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Tooltip title='Note: This refund reason will be visible to the customer' arrow>
                <HelpOutlineIcon sx={{ fontSize: 16, color: colors.brand.primary }} />
              </Tooltip>
              <Typography variant='fieldLabel'>Reason for refund (optional)</Typography>
            </Box>
            <Controller
              name='reason'
              control={control}
              render={({ field: { onChange, value } }: { field: any }) => (
                <Autocomplete
                  freeSolo
                  value={value ?? ''}
                  onChange={(_event, newValue) => onChange(newValue || '')}
                  onInputChange={(_event, newInputValue) => onChange(newInputValue)}
                  options={REFUND_REASONS}
                  renderInput={params => <TextField {...params} placeholder='Type or select reason' />}
                  renderOption={(props, option) => (
                    <li {...props} key={option} style={{ whiteSpace: 'normal' }}>
                      {option}
                    </li>
                  )}
                  slotProps={{ paper: { sx: { maxHeight: 300 } } }}
                />
              )}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button variant='ghost' onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type='submit' variant='primary' disabled={isSubmitting} sx={{ minWidth: '140px' }}>
            {isSubmitting ? 'Processing...' : 'Send refund'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
